// src/contexts/AuthStatusContext.tsx
import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

export type AuthStatus = 'loading' | 'unauth' | 'authorized';
export type GroupStatus = 'loading' | 'unauthorized' | 'applicant';

interface AuthState {
    authStatus: AuthStatus;
    groupStatus: GroupStatus;
    profile?: Record<string, unknown>;
    logout: () => Promise<void>;
}

const AuthStatusContext = createContext<AuthState | undefined>(undefined);

export const useAuthStatus = (): AuthState => {
    const ctx = useContext(AuthStatusContext);
    if (!ctx) throw new Error('useAuthStatus must be used inside <AuthStatusProvider>');
    return ctx;
};

export const AuthStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();              // from react-oidc-context
    const logout = async (): Promise<void> => {
        try {
            const clientId = process.env.REACT_APP_CLIENT_ID;
            const signOutUri = `${window.location.origin}/signout`;

            if (auth.signoutRedirect && clientId) {
                await auth.signoutRedirect({
                    post_logout_redirect_uri: signOutUri,
                    extraQueryParams: {
                        client_id: clientId,
                        logout_uri: signOutUri
                    },
                });
                return;
            }
        } catch (err) {
            console.error("signoutRedirect failed", err);
            // Even if signout fails, clear storage and redirect to signout page
            try {
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/signout';
            } catch (e) {
                console.error("Error during cleanup:", e);
            }
        }
    }
    const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
    const [groupStatus, setGroupStatus] = useState<GroupStatus>('loading');
    const [profile, setProfile] = useState<Record<string, unknown>>();

    useEffect(() => {
        if (auth.isLoading) {
            setAuthStatus('loading');
            setGroupStatus('loading');
            return;
        }
        if (!auth.isAuthenticated) {
            setAuthStatus('unauth');
            setGroupStatus('loading');
            setProfile(undefined);
            return;
        }
        // authenticated
        setAuthStatus('authorized');
        setProfile(auth.user?.profile);

        const requiredGroup = process.env.REACT_APP_REQUIRED_GROUP;
        const rawGroups: unknown = (auth.user?.profile as any)['cognito:groups'];
        const groups: string[] = Array.isArray(rawGroups)
            ? rawGroups
            : typeof rawGroups === 'string'
                ? [rawGroups]
                : [];

        console.log('[AuthStatus] Parsed groups →', groups);
        console.log('[AuthStatus] Required group →', requiredGroup);

        const isInRequiredGroup = groups.some((g) => g?.toLowerCase() === requiredGroup);
        setGroupStatus(isInRequiredGroup ? 'applicant' : 'unauthorized');


        console.log('[AuthStatus] Computed groupStatus →', isInRequiredGroup ? 'applicant' : 'unauthorized');
    }, [auth]);

    return (
        <AuthStatusContext.Provider value={{ authStatus, groupStatus, profile, logout }}>
            {children}
        </AuthStatusContext.Provider>
    );
};